<!-- Live Auctions (dev2/dev only). Approved prototype:
     docs/platex-weekend-redesign/live-auctions-prototype.html. Renders inside the
     shared nav shell; everything namespaced la-. -->
<div class="la-root">
  <main>
    <div class="la-wrap">
      <div class="la-crumb"><a routerLink="/">Home</a> › Live auctions</div>
      <section class="la-head">
        <div>
          <p class="la-eyebrow">Live auctions</p>
          <h1 class="la-h1">Every live number plate auction, in one place</h1>
          <p class="la-lede">
            Current bids from the UK's plate auction houses, with the total you'd actually pay after fees, the
            plate's previous sales and what dealers are asking for it.
          </p>
        </div>
        <div class="la-stats" *ngIf="counts">
          <div class="la-stat"><b class="num">{{ counts.live }}</b><span>Live lots</span></div>
          <div class="la-stat"><b class="num">{{ counts.today }}</b><span>Close today</span></div>
          <div class="la-stat"><b class="num">{{ gbp(liveBidTotalPence) }}</b><span>In current bids</span></div>
        </div>
      </section>

      <div class="la-cover">
        <ng-container *ngFor="let h of houses">
          <span class="la-cover-item" *ngIf="h.tracked; else offHouse">
            <span class="la-dot"></span><b>{{ h.name }}</b> {{ h.live }} live · checked {{ houseAgo(h) }}
          </span>
          <ng-template #offHouse>
            <span class="la-cover-item off"><span class="la-dot"></span>{{ h.name }} · not tracked yet</span>
          </ng-template>
        </ng-container>
        <span class="la-cover-note">Prices refresh on this page every 30 seconds</span>
      </div>

      <!-- Named sales (DVLA timed auctions, Brightwells): one card each, so two
           DVLA sales are never one undifferentiated list. -->
      <section class="la-sales" *ngIf="tabSales.length" aria-label="Auction sales">
        <article class="la-sale" *ngFor="let s of tabSales; trackBy: trackSale">
          <ng-container *ngTemplateOutlet="saleBody; context: { $implicit: s, card: true }"></ng-container>
        </article>
      </section>

      <section class="la-sale la-sale--active" *ngIf="activeSale as s" aria-label="This sale">
        <ng-container *ngTemplateOutlet="saleBody; context: { $implicit: s, card: false }"></ng-container>
      </section>
      <div class="la-sale-missing" *ngIf="state.sale && !activeSale && !loading">
        <ng-container *ngIf="state.tab === 'results'; else otherSale">Results: </ng-container>
        <ng-template #otherSale>Showing </ng-template>{{ saleTitle || 'sale ' + state.sale }}.
        <button type="button" class="la-linkbtn" (click)="clearSale()">Show all sales</button>
      </div>
    </div>

    <!-- sticky tabs + toolbar -->
    <div class="la-bar">
      <div class="la-wrap">
        <div class="la-tabs" role="tablist">
          <button
            *ngFor="let t of tabs"
            type="button"
            class="la-tab"
            role="tab"
            [attr.aria-selected]="state.tab === t.key"
            (click)="setTab(t.key)"
          >
            {{ t.label }}<small class="num" *ngIf="tabCount(t.key) !== null">{{ tabCount(t.key) }}</small>
          </button>
        </div>
        <div class="la-tools">
          <label class="la-field la-f-search">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <input
              type="search"
              placeholder="Search registrations, e.g. WT6"
              aria-label="Search registrations"
              [ngModel]="qInput"
              (ngModelChange)="onSearchInput($event)"
            />
          </label>
          <div class="la-filters">
          <div class="la-chips">
            <button
              type="button"
              class="la-chip"
              [attr.aria-pressed]="state.house === 'all'"
              (click)="setQuery({ house: 'all', sale: '', page: 1 })"
            >
              All houses <small class="num" *ngIf="houseChipCount('all') !== null">{{ houseChipCount('all') }}</small>
            </button>
            <button
              *ngFor="let h of trackedHouses"
              type="button"
              class="la-chip"
              [attr.aria-pressed]="state.house === h.key"
              (click)="setQuery({ house: h.key, sale: '', page: 1 })"
            >
              {{ h.name }} <small class="num" *ngIf="houseChipCount(h.key) !== null">{{ houseChipCount(h.key) }}</small>
            </button>
            <button
              *ngIf="isAdmin"
              type="button"
              class="la-chip la-chip--admin"
              [attr.aria-pressed]="state.rt === '1'"
              title="Admin only: Reg Transfers lots are never shown to users"
              (click)="toggleRegTransfers()"
            >
              {{ state.rt === '1' ? '✓ ' : '+ ' }}Reg Transfers <small>admin</small>
            </button>
          </div>
          <label class="la-field">
            <span class="la-lbl">Style</span>
            <select aria-label="Plate style" [ngModel]="state.style" (ngModelChange)="setQuery({ style: $event, page: 1 })">
              <option value="all">All</option>
              <option value="dateless">Dateless</option>
              <option value="current">Current</option>
              <option value="prefix">Prefix</option>
              <option value="suffix">Suffix</option>
              <option value="ni">NI</option>
            </select>
          </label>
          <label class="la-field">
            <span class="la-lbl">Price</span>
            <select aria-label="Current bid" [ngModel]="state.price" (ngModelChange)="setQuery({ price: $event, page: 1 })">
              <option *ngFor="let b of priceBands" [value]="b.key">{{ b.label }}</option>
            </select>
          </label>
          <label class="la-field">
            <span class="la-lbl">Reserve</span>
            <select aria-label="Reserve" [ngModel]="state.reserve" (ngModelChange)="setQuery({ reserve: $event, page: 1 })">
              <option value="all">Any</option>
              <option value="met">Met</option>
              <option value="not_met">Not met</option>
              <option value="unknown">Not published</option>
            </select>
          </label>
          <label class="la-field">
            <span class="la-lbl">Sort</span>
            <select aria-label="Sort" [ngModel]="state.sort" (ngModelChange)="onSortSelect($event)">
              <option value="ending">Ending soonest</option>
              <option value="high">Highest bid</option>
              <option value="low">Lowest bid</option>
              <option value="bids">Most bids</option>
              <option value="below">Furthest below last sale</option>
            </select>
          </label>
          <span class="la-spacer"></span>
          <div class="la-views" role="group" aria-label="View">
            <button type="button" [attr.aria-pressed]="state.view === 'grid'" aria-label="Card view" (click)="setView('grid')">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <rect x="3" y="3" width="8" height="8" rx="1.5" />
                <rect x="13" y="3" width="8" height="8" rx="1.5" />
                <rect x="3" y="13" width="8" height="8" rx="1.5" />
                <rect x="13" y="13" width="8" height="8" rx="1.5" />
              </svg>
            </button>
            <button type="button" [attr.aria-pressed]="state.view === 'list'" aria-label="List view" (click)="setView('list')">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <rect x="3" y="4" width="18" height="3" rx="1" />
                <rect x="3" y="10.5" width="18" height="3" rx="1" />
                <rect x="3" y="17" width="18" height="3" rx="1" />
              </svg>
            </button>
          </div>
          </div>
        </div>
      </div>
    </div>

    <div class="la-wrap">
      <div class="la-count" *ngIf="!loading && !error">
        <b class="num">{{ total }}</b> {{ countLabel }}<ng-container *ngIf="state.q"> matching "{{ state.q }}"</ng-container>
      </div>

      <div class="la-empty" *ngIf="loading && !lots.length">
        <p>Loading auction lots…</p>
      </div>

      <div class="la-empty" *ngIf="error">
        <h3>Auction lots aren't available right now</h3>
        <p>We couldn't reach the live auction feed. Try again in a moment.</p>
        <button type="button" class="la-btn-2 la-retry" (click)="load()">Try again</button>
      </div>

      <div class="la-empty" *ngIf="!loading && !error && !lots.length">
        <h3>{{ emptyCopy.title }}</h3>
        <p>{{ emptyCopy.body }}</p>
      </div>

      <!-- ========================= card grid ========================= -->
      <!-- Locked-in PlateX estimates: accuracy so far (admin-only, all-in basis, unsold excluded) -->
      <div class="la-estscore" *ngIf="showEstimates && state.tab === 'results' && shownScore as sc">
        <b>PlateX estimate accuracy{{ state.sale ? ' for this sale' : '' }}</b>
        <ng-container *ngIf="sc.scored; else noScore">
          <span>{{ sc.in_range_pct }}% sold within range</span>
          <span>median error {{ sc.median_abs_error_pct }}%</span>
          <span>{{ sc.within_25pct }}% within ±25%</span>
          <span class="la-sub">{{ sc.scored }} sold {{ sc.scored === 1 ? 'lot' : 'lots' }} scored · all-in basis · unsold excluded</span>
        </ng-container>
        <ng-template #noScore>
          <span class="la-sub" *ngIf="state.sale">No sold lots in this sale have a locked-in estimate.</span>
          <span class="la-sub" *ngIf="!state.sale">No sold results scored yet. DVLA results are confirmed from the weekly results import.</span>
        </ng-template>
      </div>

      <div class="la-grid" *ngIf="lots.length && state.view === 'grid'" [class.is-loading]="loading">
        <article
          *ngFor="let l of lots; trackBy: trackLot"
          class="la-lot"
          tabindex="0"
          [attr.aria-label]="l.dto.registration + ', ' + l.dto.houseName"
          (click)="openLot(l)"
          (keydown)="onCardKey($event, l)"
        >
          <div class="la-lot-top">
            <span class="la-house">{{ l.houseText }}</span>
            <ng-container *ngTemplateOutlet="pill; context: { $implicit: l }"></ng-container>
          </div>
          <span class="la-plate plate-font">{{ l.dto.registration }}</span>
          <div class="la-lot-price">
            <div>
              <div class="la-lbl" [class.la-lbl--keep]="l.priceLabel !== 'Current bid'">{{ l.priceLabel }}</div>
              <div class="la-bid num" [class.flash]="l.flash">{{ l.priceText }}</div>
            </div>
            <div class="la-allin" *ngIf="l.phase === 'soon'; else allIn">{{ l.dto.noVat ? 'No VAT lot' : 'VAT applies' }}</div>
            <ng-template #allIn>
              <div class="la-allin"><span class="la-allin-l">All-in&nbsp;</span><b class="num">{{ l.allInText }}</b></div>
            </ng-template>
          </div>
          <div class="la-lot-meta">
            <ng-container *ngIf="l.phase === 'soon'; else liveMeta"><span>{{ l.styleText }}</span></ng-container>
            <ng-template #liveMeta>
              <span>{{ l.bidsText }}</span><span class="la-sep">•</span><span [class]="l.reserveClass">{{ l.reserveText }}</span>
              <ng-container *ngIf="l.dto.noVat"><span class="la-sep">•</span><span>No VAT</span></ng-container>
            </ng-template>
          </div>
          <div class="la-est" *ngIf="estFor(l) as e">
            <span>PlateX est. <b class="num">{{ estRange(e) }}</b> <span class="la-sub">· {{ e.confidence }}</span></span>
            <span *ngIf="l.phase === 'ended' && estVerdict(e) as v" [class]="'la-est-v ' + v.cls">{{ v.text }}</span>
          </div>
          <div class="la-ctx" *ngIf="l.ctx.length">
            <div class="la-ctx-row" *ngFor="let r of l.ctx">
              <span>{{ r.label }}</span><span [class.none]="r.kind === 'none'" [class.hl]="r.kind === 'hl'">{{ r.value }}</span>
            </div>
          </div>
          <div class="la-lot-foot">
            <div class="la-clock"><ng-container *ngTemplateOutlet="clock; context: { $implicit: l }"></ng-container></div>
            <button
              type="button"
              class="la-ib"
              [attr.aria-pressed]="isFollowed(l)"
              [attr.aria-label]="'Notify me about ' + l.dto.registration"
              title="Notify me"
              (click)="toggleFollow(l, $event)"
            >
              <ng-container *ngTemplateOutlet="bellIcon"></ng-container>
            </button>
            <a
              *ngIf="l.phase !== 'ended'"
              class="la-go"
              [href]="l.dto.lotUrl"
              target="_blank"
              rel="noopener"
              (click)="stop($event)"
              >{{ l.phase === 'soon' ? 'View' : 'Bid' }} <ng-container *ngTemplateOutlet="extIcon"></ng-container
            ></a>
          </div>
        </article>
      </div>

      <!-- ========================= dense rows ========================= -->
      <div class="la-tbl-wrap" *ngIf="lots.length && state.view === 'list'" [class.is-loading]="loading">
        <table class="la-table">
          <thead>
            <tr>
              <th [attr.aria-sort]="sortState('registration')">
                <button type="button" class="la-th-sort" [class.is-active]="sortState('registration') !== 'none'" (click)="sortBy('registration')">
                  Registration<span class="la-th-arrow" aria-hidden="true">{{ sortState('registration') === 'ascending' ? '↑' : sortState('registration') === 'descending' ? '↓' : '↕' }}</span>
                </button>
              </th>
              <th class="d-only" [attr.aria-sort]="sortState('house')">
                <button type="button" class="la-th-sort" [class.is-active]="sortState('house') !== 'none'" (click)="sortBy('house')">
                  House<span class="la-th-arrow" aria-hidden="true">{{ sortState('house') === 'ascending' ? '↑' : sortState('house') === 'descending' ? '↓' : '↕' }}</span>
                </button>
              </th>
              <th class="d-only la-tight" [attr.aria-sort]="sortState('status')">
                <button type="button" class="la-th-sort" [class.is-active]="sortState('status') !== 'none'" (click)="sortBy('status')">
                  Status<span class="la-th-arrow" aria-hidden="true">{{ sortState('status') === 'ascending' ? '↑' : sortState('status') === 'descending' ? '↓' : '↕' }}</span>
                </button>
              </th>
              <th class="c la-tight" [attr.aria-sort]="sortState('bids')">
                <button type="button" class="la-th-sort" [class.is-active]="sortState('bids') !== 'none'" (click)="sortBy('bids')">
                  Bids<span class="la-th-arrow" aria-hidden="true">{{ sortState('bids') === 'ascending' ? '↑' : sortState('bids') === 'descending' ? '↓' : '↕' }}</span>
                </button>
              </th>
              <th class="r la-tight" [attr.aria-sort]="sortState('price')">
                <button type="button" class="la-th-sort" [class.is-active]="sortState('price') !== 'none'" (click)="sortBy('price')">
                  Current bid<span class="la-th-arrow" aria-hidden="true">{{ sortState('price') === 'ascending' ? '↑' : sortState('price') === 'descending' ? '↓' : '↕' }}</span>
                </button>
              </th>
              <th class="r d-only la-tight" [attr.aria-sort]="sortState('allin')">
                <button type="button" class="la-th-sort" [class.is-active]="sortState('allin') !== 'none'" (click)="sortBy('allin')">
                  All-in<span class="la-th-arrow" aria-hidden="true">{{ sortState('allin') === 'ascending' ? '↑' : sortState('allin') === 'descending' ? '↓' : '↕' }}</span>
                </button>
              </th>
              <th class="r d-only" *ngIf="showEstimates" title="Locked-in PlateX valuation, made before the lot ended">PlateX est.</th>
              <th [attr.aria-sort]="sortState('ending')">
                <button type="button" class="la-th-sort" [class.is-active]="sortState('ending') !== 'none'" (click)="sortBy('ending')">
                  Ends<span class="la-th-arrow" aria-hidden="true">{{ sortState('ending') === 'ascending' ? '↑' : sortState('ending') === 'descending' ? '↓' : '↕' }}</span>
                </button>
              </th>
              <th class="m-only" [attr.aria-sort]="sortState('house')">
                <button type="button" class="la-th-sort" [class.is-active]="sortState('house') !== 'none'" (click)="sortBy('house')">
                  House<span class="la-th-arrow" aria-hidden="true">{{ sortState('house') === 'ascending' ? '↑' : sortState('house') === 'descending' ? '↓' : '↕' }}</span>
                </button>
              </th>
              <th class="m-only" [attr.aria-sort]="sortState('status')">
                <button type="button" class="la-th-sort" [class.is-active]="sortState('status') !== 'none'" (click)="sortBy('status')">
                  Status<span class="la-th-arrow" aria-hidden="true">{{ sortState('status') === 'ascending' ? '↑' : sortState('status') === 'descending' ? '↓' : '↕' }}</span>
                </button>
              </th>
              <th class="r m-only" *ngIf="showEstimates" title="Locked-in PlateX valuation, made before the lot ended">PlateX est.</th>
              <th [attr.aria-sort]="sortState('lastsold')">
                <button type="button" class="la-th-sort" [class.is-active]="sortState('lastsold') !== 'none'" (click)="sortBy('lastsold')">
                  Last sold<span class="la-th-arrow" aria-hidden="true">{{ sortState('lastsold') === 'ascending' ? '↑' : sortState('lastsold') === 'descending' ? '↓' : '↕' }}</span>
                </button>
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let l of lots; trackBy: trackLot" tabindex="0" (click)="openLot(l)" (keydown)="onCardKey($event, l)">
              <td class="la-reg-cell">
                <span class="la-plate plate-font">{{ l.dto.registration }}</span>
              </td>
              <td class="d-only">{{ l.houseText }}<div class="la-sub">{{ l.styleText }}</div></td>
              <td class="d-only la-tight"><ng-container *ngTemplateOutlet="pill; context: { $implicit: l }"></ng-container></td>
              <td class="c num la-tight">
                <span class="faint" *ngIf="l.dto.bidCount == null; else bc">at close</span><ng-template #bc>{{ l.dto.bidCount }}</ng-template>
              </td>
              <td class="r la-tight">
                <span class="la-bidc num" [class.flash]="l.flash">{{ l.priceText }}</span>
                <div class="la-sub" *ngIf="l.hasPrice && l.priceLabel !== 'Current bid'">{{ l.priceLabel }}</div>
                <div class="la-sub la-res" *ngIf="l.phase !== 'soon'"><span [class]="l.reserveClass">{{ l.reserveText }}</span></div>
                <div class="la-sub m-only" *ngIf="l.phase !== 'soon' && l.hasPrice">All-in <b class="num">{{ l.allInText }}</b></div>
              </td>
              <td class="r num d-only la-tight">{{ l.phase === 'soon' ? '—' : l.allInText }}</td>
              <td class="r num d-only" *ngIf="showEstimates">
                <ng-container *ngIf="estFor(l) as e; else noEst">
                  {{ estRange(e) }}
                  <div class="la-sub">{{ e.confidence }}</div>
                  <div *ngIf="l.phase === 'ended' && estVerdict(e) as v" [class]="'la-est-v ' + v.cls">{{ v.text }}</div>
                </ng-container>
                <ng-template #noEst><span class="faint">—</span></ng-template>
              </td>
              <td><ng-container *ngTemplateOutlet="clock; context: { $implicit: l, row: true }"></ng-container></td>
              <td class="m-only">{{ l.houseText }}<div class="la-sub">{{ l.styleText }}</div></td>
              <td class="m-only"><ng-container *ngTemplateOutlet="pill; context: { $implicit: l }"></ng-container></td>
              <td class="r num m-only" *ngIf="showEstimates">
                <ng-container *ngIf="estFor(l) as e; else noEstM">
                  {{ estRange(e) }}
                  <div class="la-sub">{{ e.confidence }}</div>
                  <div *ngIf="l.phase === 'ended' && estVerdict(e) as v" [class]="'la-est-v ' + v.cls">{{ v.text }}</div>
                </ng-container>
                <ng-template #noEstM><span class="faint">—</span></ng-template>
              </td>
              <td class="num">
                <ng-container *ngIf="l.dto.lastSold; else noLast">
                  {{ gbp(l.dto.lastSold.pricePence) }} <span class="la-sub">{{ monthYear(l.dto.lastSold.date) }}</span>
                </ng-container>
                <ng-template #noLast><span class="faint">—</span></ng-template>
              </td>
              <td class="r">
                <button
                  type="button"
                  class="la-ib"
                  [attr.aria-pressed]="isFollowed(l)"
                  [attr.aria-label]="'Notify me about ' + l.dto.registration"
                  (click)="toggleFollow(l, $event)"
                >
                  <ng-container *ngTemplateOutlet="bellIcon"></ng-container>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <nav class="la-pager" *ngIf="pagedByApi" aria-label="Pages">
        <button type="button" class="la-btn-2" [disabled]="state.page <= 1" (click)="goPage(state.page - 1)">‹ Previous</button>
        <span class="num">Page {{ state.page }} of {{ totalPages }}</span>
        <button type="button" class="la-btn-2" [disabled]="state.page >= totalPages" (click)="goPage(state.page + 1)">Next ›</button>
      </nav>

      <!-- ========================= fees + notes ========================= -->
      <section class="la-lower">
        <div>
          <h2>What you'll actually pay</h2>
          <p class="la-muted la-lower-lede">
            Each lot shows an all-in figure: the current bid plus that auction house's buyer's fees and VAT. Check the
            house's own terms before bidding.
          </p>
          <div class="la-x-scroll">
            <table class="la-fees">
              <thead>
                <tr><th>Auction house</th><th>Buyer pays on top of the hammer price</th></tr>
              </thead>
              <tbody>
                <tr><td>Reg Transfers</td><td>No buyer's premium. £80 DVLA transfer fee, plus VAT on lots not marked "No VAT".</td></tr>
                <tr><td>Collecting Cars</td><td>10% buyer's premium + VAT, minimum £300 including VAT.</td></tr>
                <tr><td>DVLA timed auction</td><td>VAT on the hammer, a buyer's premium + VAT, and the £80 assignment fee.</td></tr>
                <tr><td>Brightwells</td><td>Buyer's premium + VAT, with a minimum charge.</td></tr>
              </tbody>
            </table>
          </div>
        </div>
        <aside class="la-notes">
          <b>About this data</b>
          <ul>
            <li>Lots are checked regularly and may lag the auction house's own page by a few minutes.</li>
            <li>Bids are placed on the auction house's website. PlateX does not take bids.</li>
            <li>Reg Transfers doesn't publish reserve status or live bid counts, so those lots say "Reserve not published" and show bids at the close.</li>
            <li>Previous sales come from PlateX sold data; dealer prices from the PlateX price comparison.</li>
          </ul>
        </aside>
      </section>
    </div>
  </main>

  <!-- ========================= lot drawer ========================= -->
  <ng-container *ngIf="drawer as l">
    <div class="la-scrim" (click)="closeLot()"></div>
    <aside class="la-drawer" role="dialog" aria-modal="true" [attr.aria-label]="l.dto.registration + ' lot details'">
      <div class="la-d-head">
        <div class="la-house">{{ l.houseText }}</div>
        <div class="la-d-head-r">
          <ng-container *ngTemplateOutlet="pill; context: { $implicit: l }"></ng-container>
          <button type="button" class="la-x" aria-label="Close" (click)="closeLot()">×</button>
        </div>
      </div>
      <div class="la-d-body">
        <span class="la-plate la-d-plate plate-font">{{ l.dto.registration }}</span>

        <div class="la-d-row">
          <div class="la-d-box">
            <div class="la-lbl">{{ l.priceLabel }}</div>
            <div class="la-bid num" [class.flash]="l.flash">{{ l.priceText }}</div>
            <div class="la-small faint">
              {{ l.dto.bidCount == null ? 'Bid count at close' : l.bidsText }} · {{ l.reserveText.toLowerCase() }}
            </div>
          </div>
          <div class="la-d-box">
            <div class="la-lbl">{{ l.phase === 'soon' ? 'Opens' : l.phase === 'ended' ? 'Closed' : 'Time left' }}</div>
            <div class="la-bid la-bid--sm num" [class.urgent]="l.phase === 'closing'">
              {{ l.phase === 'soon' ? timeOfDay(l.opensAt) : l.phase === 'ended' ? timeOfDay(l.closesAt) : l.leftText }}
            </div>
            <div class="la-small faint">
              {{ clockDay(l) }}<ng-container *ngIf="l.dto.house === 'regtransfers'"> · 5-min bids extend 15 min</ng-container
              ><ng-container *ngIf="l.dto.house === 'collecting_cars'"> · 4-min stagger</ng-container>
            </div>
          </div>
        </div>

        <div class="la-d-sec" *ngIf="l.phase !== 'soon' && l.hasPrice">
          <h4>All-in cost at the {{ l.priceLabel.toLowerCase() }}</h4>
          <div class="la-kv" *ngIf="detail?.feeBreakdown?.length; else feeFallback">
            <ng-container *ngFor="let f of feeRows">
              <span>{{ f.label }}<span class="faint" *ngIf="f.note"> ({{ f.note }})</span></span><span class="num">{{ gbp(f.pence) }}</span>
            </ng-container>
            <span class="la-tot">You would pay</span><span class="la-tot num">{{ gbp(feeTotal) }}</span>
          </div>
          <ng-template #feeFallback>
            <div class="la-kv">
              <span>{{ l.priceLabel }}</span><span class="num">{{ l.priceText }}</span>
              <span class="la-tot">You would pay (estimate)</span><span class="la-tot num">{{ l.allInText }}</span>
            </div>
          </ng-template>
        </div>

        <div class="la-d-sec">
          <h4>Bid history</h4>
          <table class="la-bh" *ngIf="detail?.bidHistory?.length; else noBh">
            <tr *ngFor="let b of detail!.bidHistory">
              <td class="num">{{ gbp(b.amountPence) }}</td>
              <!-- Reg Transfers never gives exact times (at = null): relative text, else approxAt -->
              <td *ngIf="b.at; else approxTime">{{ b.at | date: 'd MMM, HH:mm' }}</td>
              <ng-template #approxTime>
                <td>{{ b.relative || (b.approxAt ? '≈ ' + (b.approxAt | date: 'd MMM, HH:mm') : '—') }}</td>
              </ng-template>
            </tr>
          </table>
          <ng-template #noBh>
            <div class="la-callout" *ngIf="!detailLoading">
              <ng-container *ngIf="l.dto.house === 'regtransfers'; else ccBh"
                >Reg Transfers shows bid history on the lot page. PlateX collects it as the lot closes and shows it here.</ng-container
              >
              <ng-template #ccBh>Bid history from {{ l.dto.houseName }} appears here as PlateX checks the lot.</ng-template>
            </div>
            <div class="la-callout" *ngIf="detailLoading">Loading…</div>
          </ng-template>
        </div>

        <div class="la-d-sec">
          <h4>Earlier auction attempts</h4>
          <ng-container *ngIf="attemptsSummary as a; else noAttempts">
            <div class="la-callout">
              Listed <b>{{ a.count + 1 }} times</b> since {{ a.since }}.
              <ng-container *ngIf="!a.sold">None of the {{ a.count }} earlier attempts sold.</ng-container>
              <ng-container *ngIf="a.best"> The highest closing bid was <b>{{ gbp(a.best) }}</b> ({{ a.bestAt }}).</ng-container>
            </div>
            <svg
              *ngIf="spark as s"
              class="la-spark"
              [attr.viewBox]="'0 0 ' + s.w + ' ' + s.h"
              role="img"
              aria-label="Closing bid at each earlier attempt, ending with the current bid"
            >
              <ng-container *ngFor="let g of s.grid">
                <line [attr.x1]="s.p" [attr.x2]="s.w - s.p" [attr.y1]="g.y" [attr.y2]="g.y" stroke="#e9e7e2" />
                <text [attr.x]="s.p - 4" [attr.y]="g.y + 4" text-anchor="end" class="la-svg-t">{{ g.label }}</text>
              </ng-container>
              <path [attr.d]="s.path" fill="none" stroke="#0e2b33" stroke-width="1.6" />
              <circle *ngFor="let d of s.dots" [attr.cx]="d.x" [attr.cy]="d.y" [attr.r]="d.r" [attr.fill]="d.fill" />
              <text [attr.x]="s.p" [attr.y]="s.h - 3" class="la-svg-t">{{ s.firstLabel }}</text>
              <text [attr.x]="s.w - s.p" [attr.y]="s.h - 3" text-anchor="end" class="la-svg-t">Now</text>
            </svg>
          </ng-container>
          <ng-template #noAttempts>
            <div class="la-callout" *ngIf="!detailLoading">No earlier auction attempts recorded for this registration.</div>
          </ng-template>
        </div>

        <div class="la-d-sec" *ngIf="l.dto.lastSold || l.dto.dealerBest">
          <h4>Previous sales and offers</h4>
          <div class="la-kv">
            <ng-container *ngIf="l.dto.lastSold">
              <span>Last sold</span>
              <span>{{ gbp(l.dto.lastSold.pricePence) }} · {{ monthYear(l.dto.lastSold.date) }}</span>
            </ng-container>
            <ng-container *ngIf="l.dto.dealerBest">
              <span>Previously offered at</span>
              <span>{{ l.dto.dealerBest.pricePence == null ? 'POA' : gbp(l.dto.dealerBest.pricePence) }} · {{ l.dto.dealerBest.dealer }}</span>
            </ng-container>
          </div>
        </div>

        <a class="la-history-link" [routerLink]="plateDetailLink(l)" (click)="closeLot()">Full plate history on PlateX →</a>
      </div>
      <div class="la-d-actions">
        <button type="button" class="la-btn-2" [attr.aria-pressed]="isFollowed(l)" (click)="toggleFollow(l, $event)">
          <ng-container *ngTemplateOutlet="bellIcon"></ng-container>{{ isFollowed(l) ? 'Following' : 'Notify me' }}
        </button>
        <a *ngIf="l.phase !== 'ended'" class="la-go" [href]="l.dto.lotUrl" target="_blank" rel="noopener"
          >{{ l.phase === 'soon' ? 'View on ' : 'Bid on ' }}{{ l.dto.houseName }} <ng-container *ngTemplateOutlet="extIcon"></ng-container
        ></a>
      </div>
    </aside>
  </ng-container>
</div>

<!-- ========================= shared fragments ========================= -->
<ng-template #pill let-l>
  <span class="la-status" [ngClass]="l.pillClass"
    ><i *ngIf="l.phase === 'live' || l.phase === 'closing'"></i>{{ l.pillText }}</span
  >
</ng-template>

<ng-template #clock let-l let-row="row">
  <ng-container [ngSwitch]="l.phase">
    <ng-container *ngSwitchCase="'soon'">
      <b>{{ clockDay(l) }} {{ timeOfDay(l.opensAt) }}</b>
      <small [class.la-sub]="row">Bidding opens · runs 7 days</small>
    </ng-container>
    <ng-container *ngSwitchCase="'ended'">
      <b>{{ l.endedText }}</b>
      <small [class.la-sub]="row">{{ endedSub(l) }}</small>
    </ng-container>
    <ng-container *ngSwitchDefault>
      <b class="num" [class.urgent]="l.phase === 'closing'">{{ l.leftText }}</b>
      <small [class.la-sub]="row">{{ clockDay(l) }} {{ timeOfDay(l.closesAt) }}</small>
    </ng-container>
  </ng-container>
</ng-template>

<ng-template #bellIcon>
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.7 21a2 2 0 0 1-3.4 0" />
  </svg>
</ng-template>

<!-- One sale: card (list of sales) or header (?sale=) -->
<ng-template #saleBody let-s let-card="card">
  <div class="la-sale-top">
    <span class="la-house">{{ s.houseName }} · {{ s.label }}</span>
    <span class="la-sale-status" [class.is-live]="s.status === 'live'">{{ s.status === 'live' ? 'Bidding open' : 'Coming soon' }}</span>
  </div>
  <h2 class="la-sale-title">{{ s.title }}</h2>
  <p class="la-sale-when">
    {{ saleWhen(s) }}<span class="la-sale-count"> · {{ saleCountdown(s) }}</span>
  </p>
  <p class="la-sale-lots num">{{ s.lotCount | number }} plates</p>
  <div class="la-sale-picks" *ngIf="s.picks?.length">
    <span class="la-lbl">Worth a look</span>
    <a class="la-pick" *ngFor="let p of s.picks" [routerLink]="p.targetUrl" (click)="$event.stopPropagation()">
      <span class="la-plate plate-font">{{ p.formattedRegistration || p.registration }}</span>
      <span class="la-pick-why" [class.is-match]="p.kind === 'match'">{{ p.label }}</span>
    </a>
  </div>
  <div class="la-sale-actions">
    <button *ngIf="card" type="button" class="la-go" (click)="selectSale(s)">View all {{ s.lotCount | number }} lots</button>
    <button *ngIf="!card" type="button" class="la-btn-2" (click)="clearSale()">← All sales</button>
    <a *ngIf="s.officialUrl" class="la-btn-2" [href]="s.officialUrl" target="_blank" rel="noopener"
      >{{ s.status === 'live' ? 'Bid on ' : 'View on ' }}{{ saleSiteName(s) }} <ng-container *ngTemplateOutlet="extIcon"></ng-container
    ></a>
  </div>
</ng-template>

<ng-template #extIcon>
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
    <path d="M14 4h6v6" />
    <path d="M20 4 10 14" />
    <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
  </svg>
</ng-template>
